import { LIMITS, OCCASION_CONFIG } from "@listou/shared/constants";
import type { GroupResponse } from "@listou/shared/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { DrawCard } from "../components/group/draw-card";
import { DrawGrid } from "../components/group/draw-grid";
import { InviteCard } from "../components/group/invite-card";
import { PeopleRows } from "../components/group/people-rows";
import { useGroupAction } from "../components/group/use-group-action";
import { Dialog } from "../components/ui/dialog";
import { PageLoader } from "../components/ui/spinner";
import { ApiError, groupsApi } from "../lib/api";
import { countdownLabel, todayInFrance } from "../lib/format";
import { groupQuery, groupsQuery } from "../lib/queries";
import { errorMessage } from "../lib/session";

export const Route = createFileRoute("/_authed/mes-groupes/$groupId")({
  loader: async ({ context: { queryClient }, params: { groupId } }) => {
    try {
      await queryClient.query(groupQuery(groupId));
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) throw notFound();
      throw error;
    }
  },
  component: ManageGroupPage,
});

function ManageGroupPage() {
  const { groupId } = Route.useParams();
  const query = useQuery(groupQuery(groupId));

  if (query.isPending) return <PageLoader />;
  if (query.isError) {
    return (
      <p className="mx-auto max-w-3xl px-4 py-12 text-danger">
        {errorMessage(query.error, "Le groupe n'a pas pu être chargé.")}
      </p>
    );
  }
  const data = query.data;
  const { group } = data;
  const santa = group.mode === "secret_santa";
  const countdown = countdownLabel(group.eventDate);

  /*
   * One column on a phone, in the order things are done: the draw, the people, the draw
   * rules, the settings. On a computer the people and their rules take the width, and
   * the draw sits beside them.
   */
  return (
    <div className="mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)] gap-x-10 gap-y-8 px-4 pt-8 pb-28 sm:px-6 sm:pt-10 lg:grid-cols-[minmax(0,1fr)_23rem] lg:pb-16">
      <header className="grid gap-2 lg:col-span-2">
        <Link to="/mes-listes" className="btn-ghost btn-sm -ml-3 justify-self-start">
          ← Mes listes
        </Link>
        <h1 className="text-4xl font-semibold sm:text-5xl">{group.title}</h1>
        <p className="text-lg text-muted">
          {santa
            ? "Secret Santa"
            : group.mode === "collection"
              ? "Vos listes, sous un seul lien"
              : group.occasion !== "other"
                ? OCCASION_CONFIG[group.occasion].label
                : "Groupe de listes"}
          {countdown ? `, ${countdown.toLowerCase()}` : ""}
        </p>
      </header>

      <aside className="grid content-start gap-4 lg:col-start-2 lg:row-start-2">
        {santa && <DrawCard data={data} />}
        <InviteCard data={data} />
      </aside>

      <div className="grid min-w-0 content-start gap-10 lg:col-start-1 lg:row-span-3 lg:row-start-2">
        <PeopleRows data={data} />
        {santa && <DrawGrid data={data} />}
      </div>

      <div className="lg:col-start-2 lg:row-start-3">
        <SettingsPanel data={data} />
      </div>
    </div>
  );
}

function SettingsPanel({ data }: { data: GroupResponse }) {
  const { group } = data;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(group.title);
  const [eventDate, setEventDate] = useState(group.eventDate ?? "");
  const [organizerName, setOrganizerName] = useState(group.organizerName);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const nextYear = Number((group.eventDate ?? new Date().toISOString()).slice(0, 4)) + 1;

  const update = useGroupAction(
    group.id,
    (patch: Parameters<typeof groupsApi.update>[1]) => groupsApi.update(group.id, patch),
    { success: "Enregistré." },
  );
  const rotate = useGroupAction(group.id, () => groupsApi.rotateSlug(group.id), {
    success: "Nouveau lien : l'ancien ne marche plus.",
  });
  const duplicate = useMutation({
    mutationFn: () =>
      groupsApi.duplicate(group.id, {
        title: group.title.replace(/\b20\d\d\b/, String(nextYear)),
        eventDate: group.eventDate ? `${nextYear}${group.eventDate.slice(4)}` : null,
      }),
    onSuccess: ({ group: next }) => {
      void queryClient.invalidateQueries({ queryKey: groupsQuery.queryKey });
      void navigate({ to: "/mes-groupes/$groupId", params: { groupId: next.id } });
    },
  });
  const remove = useMutation({
    mutationFn: () => groupsApi.remove(group.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groupsQuery.queryKey });
      void navigate({ to: "/mes-listes" });
    },
  });

  const over = group.eventDate ? group.eventDate < todayInFrance() : Boolean(group.drawnAt);
  const dateLabel = group.eventDate
    ? new Date(`${group.eventDate}T12:00:00`).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "sans date";

  return (
    <section className="grid gap-3 rounded-[1.75rem] bg-surface p-4 ring-[1.5px] ring-line">
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-0.5">
          <p className="font-bold">Le groupe</p>
          <p className="text-sm text-muted">
            {dateLabel} · signé {group.organizerName}
          </p>
        </div>
        <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing(true)}>
          Modifier
        </button>
      </div>
      <div className="flex flex-wrap gap-x-1 gap-y-0.5 border-t-[1.5px] border-line pt-3">
        {/* Next year's group only makes sense once this one's day has passed. */}
        {over && (
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => duplicate.mutate()}
            disabled={duplicate.isPending}
          >
            Préparer {nextYear}
          </button>
        )}
        <button type="button" className="btn-ghost btn-sm" onClick={() => rotate.mutate(undefined)}>
          Changer le lien
        </button>
        <button
          type="button"
          className="btn-ghost btn-sm text-danger"
          onClick={() => setConfirmDelete(true)}
        >
          Supprimer
        </button>
      </div>
      {(duplicate.isError || remove.isError) && (
        <p className="text-danger">
          {errorMessage(duplicate.error ?? remove.error, "Ça n'a pas marché.")}
        </p>
      )}

      <Dialog open={editing} onClose={() => setEditing(false)} title="Le groupe">
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            update.mutate(
              { title, organizerName, eventDate: eventDate || null },
              { onSuccess: () => setEditing(false) },
            );
          }}
        >
          <label className="label">
            Nom du groupe
            <input
              className="input"
              maxLength={LIMITS.groupTitleMax}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <label className="label">
            Votre prénom, qui signe le groupe
            <input
              className="input"
              maxLength={LIMITS.personNameMax}
              value={organizerName}
              onChange={(event) => setOrganizerName(event.target.value)}
            />
          </label>
          <label className="label">
            Date
            <input
              className="input"
              type="date"
              value={eventDate}
              onChange={(event) => setEventDate(event.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="submit" className="btn" disabled={update.isPending}>
              Enregistrer
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(false)}>
              Annuler
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Supprimer le groupe ?"
        description="Les listes que les gens ont remplies restent à eux. Le lien du groupe ne marchera plus."
      >
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-danger"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
          >
            Supprimer définitivement
          </button>
          <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(false)}>
            Annuler
          </button>
        </div>
      </Dialog>
    </section>
  );
}
